import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Stack,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../redux/actions';
import useMarketingLiveRefresh from '../../hooks/useMarketingLiveRefresh';
import {
  STATUS_LABELS, STATUS_ICONS, getStatusHex, getStatusChipSx,
} from '../../constants/status';
import { formatDate, formatValue } from '../../utils/format';
import { OpportunityCalendarModal } from '../dialogs/OpportunityCalendarModal';
import { formatEmployeeThaiName } from '../../../../utils/functions/employeeName';
import EmployeeAvatar from '../../../../components/EmployeeAvatar';

// สถานะที่ยังถือว่าเป็น "งาน" ของผู้รับผิดชอบ (ยังไม่จบดีล) — ตัด WON/LOST/CANCELLED ออก
// ทั้งจากการ์ดสรุปและ modal รายการงาน
const OPEN_STATUSES = ['WAITING', 'IN_PROGRESS', 'ON_HOLD'];

// "เหลืออีกกี่วันถึงวันเสนอราคา" มีความหมายเฉพาะตอนดีลยังเปิดอยู่จริง (ยังไม่เริ่ม/กำลังทำ) —
// สถานะ ON_HOLD (ส่งเสนอราคาลูกค้าแล้ว) วันครบกำหนดเดิมไม่มีความหมายให้นับถอยหลังอีกต่อไป ซ่อน
// บรรทัดนี้ทิ้งไปเลยแทนโชว์ค้าง — เหมือน pattern เดียวกับ ACTIVE_QUOTATION_COUNTDOWN_STATUSES ใน
// OpportunityTaskListModal.js
const ACTIVE_QUOTATION_COUNTDOWN_STATUSES = ['WAITING', 'IN_PROGRESS'];

// แท็บ "งานของผู้รับผิดชอบ" — ดึงเฉพาะโอกาสทางธุรกิจที่ยังไม่จบดีล (OPEN_STATUSES) แค่ครั้งเดียว
// ตอนเปิดแท็บนี้ครั้งแรก แล้ว group ฝั่ง client ด้วย owner_id — คนละ fetch กับตารางหลัก
// (useOpportunities) ที่มี infinite scroll/filter ของตัวเอง ไม่ยุ่งเกี่ยวกัน
export function OwnerOpportunitiesTab({ employeeById, onOpportunityClick }) {
  const dispatch = useDispatch();
  const [rows, setRows] = useState(null); // null = ยังไม่โหลด
  const [loading, setLoading] = useState(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState(null);
  // แยกจาก selectedOwnerId (modal รายการงาน) — เก็บ group ที่กดไอคอนปฏิทินบนการ์ด เปิดปฏิทินกรอง
  // เฉพาะคนนั้นได้โดยไม่ต้องผ่าน modal รายการงานก่อน
  const [calendarOwnerGroup, setCalendarOwnerGroup] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    dispatch(actions.listOpportunities({
      status: OPEN_STATUSES.join(','),
      size: 9999,
      sortBy: 'created_at',
      sortOrder: 'desc',
    })).then((data) => {
      if (!cancelled) setRows(data.data || []);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [dispatch]);

  // เห็นการแก้จากที่อื่นเอง (เงียบ ไม่โชว์ spinner/error)
  useMarketingLiveRefresh(async () => {
    const data = await dispatch(actions.listOpportunities({
      status: OPEN_STATUSES.join(','),
      size: 9999,
      sortBy: 'created_at',
      sortOrder: 'desc',
      silent: true,
    }));
    const next = data.data || [];
    setRows((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  }, { enabled: rows !== null });

  // group ตาม owner_id → เรียงคนที่มีงานเยอะสุดก่อน ตัดแถวที่ไม่มี owner_id ทิ้ง
  const groups = useMemo(() => {
    const map = {};
    (rows || []).forEach((row) => {
      if (!row.owner_id) return;
      if (!map[row.owner_id]) map[row.owner_id] = [];
      map[row.owner_id].push(row);
    });
    return Object.entries(map)
      .map(([ownerId, groupRows]) => {
        const emp = employeeById[ownerId];
        const statusCounts = {};
        groupRows.forEach((row) => {
          statusCounts[row.status] = (statusCounts[row.status] || 0) + 1;
        });
        // เรียงหลัก: ตามกลุ่มสถานะก่อน (ยังไม่เริ่ม → กำลังทำ → ส่งลูกค้าแล้ว ตามลำดับใน
        // OPEN_STATUSES เป๊ะ — ไม่ต้องทำ mapping แยกอีกชุด) เรียงรอง: ภายในสถานะเดียวกันเรียงตาม
        // วันที่เสนอราคาใกล้ถึงก่อน — รายการที่ยังไม่มีวันที่เสนอราคาเลย (ยังไม่ระบุ) ดันไปท้ายกลุ่ม
        // สถานะเดียวกันเสมอ ไม่ปนกับรายการที่มีวันที่จริงด้านบน
        const items = [...groupRows].sort((a, b) => {
          const statusDiff = OPEN_STATUSES.indexOf(a.status) - OPEN_STATUSES.indexOf(b.status);
          if (statusDiff !== 0) return statusDiff;
          if (!a.quotation_due_date && !b.quotation_due_date) return 0;
          if (!a.quotation_due_date) return 1;
          if (!b.quotation_due_date) return -1;
          return dayjs(a.quotation_due_date).valueOf() - dayjs(b.quotation_due_date).valueOf();
        });
        return {
          ownerId,
          employee: emp,
          name: emp ? formatEmployeeThaiName(emp) : '(ไม่ทราบชื่อ)',
          department: emp?.department?.name,
          items,
          statusCounts,
        };
      })
      .sort((a, b) => b.items.length - a.items.length);
  }, [rows, employeeById]);

  const selectedGroup = groups.find((g) => g.ownerId === selectedOwnerId) || null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (groups.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
        <InboxOutlinedIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
        <Typography variant="body2">ยังไม่มีผู้รับผิดชอบถูกกำหนด</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 2,
        }}
      >
        {groups.map((group) => (
          <Paper
            key={group.ownerId}
            elevation={0}
            onClick={() => setSelectedOwnerId(group.ownerId)}
            sx={{
              position: 'relative',
              p: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 0.5,
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: (theme) => (theme.palette.mode === 'dark'
                  ? '0 8px 24px rgba(0,0,0,0.4)'
                  : '0 8px 24px rgba(15,23,42,0.1)'),
                transform: 'translateY(-2px)',
              },
            }}
          >
            <IconButton
              size="small"
              title={`ปฏิทินของ ${group.name}`}
              onClick={(e) => { e.stopPropagation(); setCalendarOwnerGroup(group); }}
              sx={{ position: 'absolute', top: 4, right: 4, color: 'text.secondary' }}
            >
              <EventOutlinedIcon fontSize="small" />
            </IconButton>
            <EmployeeAvatar employee={group.employee} size={64} sx={{ mb: 0.5 }} />
            <Typography fontWeight={700}>{group.name}</Typography>
            {group.department && (
              <Typography variant="caption" color="text.secondary">{group.department}</Typography>
            )}
            <Chip label={`${group.items.length} งาน`} size="small" color="primary" sx={{ mt: 1 }} />
            <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="center" sx={{ mt: 0.5 }}>
              {Object.entries(group.statusCounts).map(([status, count]) => (
                <Chip
                  key={status}
                  label={`${STATUS_LABELS[status]} ${count}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', ...getStatusChipSx(status, 'outlined') }}
                />
              ))}
            </Stack>
          </Paper>
        ))}
      </Box>

      <Dialog
        open={!!selectedGroup}
        onClose={() => setSelectedOwnerId(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.5,
            py: 2,
            background: 'linear-gradient(135deg, rgb(var(--color-primary-800)) 0%, rgb(var(--color-primary-600)) 52%, rgb(var(--color-primary-450)) 100%)',
            color: '#fff',
          }}
        >
          <EmployeeAvatar
            employee={selectedGroup?.employee}
            size={48}
            sx={{
              border: '2px solid rgba(255,255,255,0.4)',
              bgcolor: 'rgba(255,255,255,0.16)',
              color: '#fff',
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.68)', letterSpacing: 0.5 }}>
              งานของผู้รับผิดชอบ
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {selectedGroup?.name}
            </Typography>
            {selectedGroup?.department && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.68)' }}>
                {selectedGroup.department}
              </Typography>
            )}
          </Box>
          <Chip
            label={`${selectedGroup?.items.length || 0} งาน`}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 700 }}
          />
          <IconButton
            onClick={() => setSelectedOwnerId(null)}
            size="small"
            sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, bgcolor: (theme) => (theme.palette.mode === 'light' ? '#f8fafc' : 'background.default') }}>
          <List disablePadding>
            {selectedGroup?.items.map((row) => {
              const Icon = STATUS_ICONS[row.status];
              return (
                <ListItemButton
                  key={row.id}
                  divider
                  onClick={() => onOpportunityClick?.(row.id)}
                  sx={{
                    py: 1.5,
                    alignItems: 'flex-start',
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    <Icon fontSize="small" sx={{ color: getStatusHex(row.status) }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={(
                      <Typography variant="body2" fontWeight={700}>
                        {row.project_name}
                      </Typography>
                    )}
                    secondary={(
                      <>
                        <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                          {row.opportunity_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                          {row.contact_name_snapshot || '-'}
                          {' · '}
                          {formatValue(row.value)}
                        </Typography>
                        {row.quotation_due_date && ACTIVE_QUOTATION_COUNTDOWN_STATUSES.includes(row.status) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                            sx={{ display: 'block' }}
                          >
                            เสนอราคา {formatDate(row.quotation_due_date)}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                  <Chip
                    label={STATUS_LABELS[row.status]}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 1, mt: 0.5, ...getStatusChipSx(row.status, 'outlined') }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>

      <OpportunityCalendarModal
        open={!!calendarOwnerGroup}
        onClose={() => setCalendarOwnerGroup(null)}
        onOpportunityClick={onOpportunityClick}
        ownerId={calendarOwnerGroup?.ownerId}
        title={`ปฏิทินของ ${calendarOwnerGroup?.name || ''}`}
      />
    </Box>
  );
}

export default OwnerOpportunitiesTab;
